import { InfiniteData } from '@tanstack/react-query';

import {
  DispensableMedicineModel,
  EPharmacyMainTagType,
  EPharmacyParkingType,
  EPharmacyPriceTagType,
  EPharmacyTagType,
  MedicineInfoWithDoseDaysModel,
  NearbyPharmacyFilterModel,
  PharmacyBadgeModel,
  PharmacyDispensableMedicineModel,
  PharmacyDutyModel,
  PharmacyMainTagModel,
  PharmacyModel,
  PharmacyNextOperationHourModel,
  PharmacyPriceTagModel,
  PharmacyReviewModel,
  PharmacyTagModel,
} from '@/models/pharmacy';
import { Review, ReviewTag, ReviewTagVo } from '@/models/review/review';
import TreatmentMedicineService from '@/services/pharmacy/TreatmentMedicineService';
import {
  FindGeneralListOfNearbyPharmacyV3ByUserQuery,
  FindPickupListOfNearbyPharmacyV3ByUserQuery,
  GeneralNearbyPharmacySearchTabsQuery,
  GetNearbyPharmacyDetailV3ByUserQuery,
  PharmacyFilterMode,
  PharmacyFilterType,
  PharmacyOperationStatusV2,
  PharmacyParkingType,
  PharmacyReviewTagsByRatingQuery,
  PharmacyReviewsQuery,
  PharmacySortType,
  PickupNearbyPharmacySearchTabQuery,
  TreatmentMedicinesByTreatmentIdQuery,
} from '@/types/bff/gql-schema';
import {
  ENearbyPharmacySearchFilterType,
  ENearbyPharmacySearchSortType,
  EPharmacyFilterMode,
  EPharmacyOperationType,
  NearbyPharmacyMedicineVo,
  NearbyPharmacySearchTabsVo,
  NearbyPharmacySortsAndFiltersVo,
  NearbyPharmacySortsVo,
} from '@/types/pharmacy';
import { formatPriceComma } from '@/utils/format';

// 타입 변환 매퍼 함수
const mapToPharmacyFilterMode = (mode: PharmacyFilterMode): EPharmacyFilterMode => {
  switch (mode) {
    case PharmacyFilterMode.List:
      return EPharmacyFilterMode.List;
    case PharmacyFilterMode.Toggle:
      return EPharmacyFilterMode.Toggle;
    case PharmacyFilterMode.Range:
      return EPharmacyFilterMode.Range;
    default:
      return EPharmacyFilterMode.Toggle;
  }
};

const mapToPharmacySearchFilterType = (type: PharmacyFilterType): ENearbyPharmacySearchFilterType => {
  switch (type) {
    case PharmacyFilterType.ConfirmedDispensation:
      return ENearbyPharmacySearchFilterType.ConfirmedDispensation;
    case PharmacyFilterType.DispensationProbability:
      return ENearbyPharmacySearchFilterType.DispensationProbability;
    case PharmacyFilterType.HandlingMedicine:
      return ENearbyPharmacySearchFilterType.HandlingMedicine;
    case PharmacyFilterType.HighDispensationProbability:
      return ENearbyPharmacySearchFilterType.HighDispensationProbability;
    case PharmacyFilterType.IncludeDiagnosisPrice:
      return ENearbyPharmacySearchFilterType.IncludeDiagnosisPrice;
    case PharmacyFilterType.IsOpenAtLateNight:
      return ENearbyPharmacySearchFilterType.IsOpenAtLateNight;
    case PharmacyFilterType.IsOpenEveryDay:
      return ENearbyPharmacySearchFilterType.IsOpenEveryDay;
    case PharmacyFilterType.Now:
      return ENearbyPharmacySearchFilterType.Now;
    case PharmacyFilterType.Operating:
      return ENearbyPharmacySearchFilterType.Operating;
    case PharmacyFilterType.PossibleDispensation:
      return ENearbyPharmacySearchFilterType.PossibleDispensation;
    case PharmacyFilterType.RequestWithApp:
      return ENearbyPharmacySearchFilterType.RequestWithApp;
    case PharmacyFilterType.SameMedicine:
      return ENearbyPharmacySearchFilterType.SameMedicine;
    default:
      return ENearbyPharmacySearchFilterType.DispensationProbability;
  }
};

const mapToPharmacySortsVo = (sort: {
  type: PharmacySortType;
  title: string;
}): NearbyPharmacySortsVo => {
  let sortType: ENearbyPharmacySearchSortType;

  switch (sort.type) {
    case PharmacySortType.Distance:
      sortType = ENearbyPharmacySearchSortType.Distance;
      break;
    case PharmacySortType.LowPrice:
      sortType = ENearbyPharmacySearchSortType.LowPrice;
      break;
    case PharmacySortType.QuickReceipt:
      sortType = ENearbyPharmacySearchSortType.QuickReceipt;
      break;
    case PharmacySortType.VisitCount:
      sortType = ENearbyPharmacySearchSortType.VisitCount;
      break;
    case PharmacySortType.MostReviews:
      sortType = ENearbyPharmacySearchSortType.MostReviews;
      break;
    case PharmacySortType.HighRating:
      sortType = ENearbyPharmacySearchSortType.HighRating;
      break;
    default:
      sortType = ENearbyPharmacySearchSortType.QuickReceipt;
  }

  return {
    title: sort.title,
    type: sortType,
  };
};

export const parsePharmacyMainTagType = (type: string): EPharmacyMainTagType => {
  switch (type) {
    case 'HIGH_DISPENSATION_PROBABILITY':
      return EPharmacyMainTagType.HighDispensationProbability;
    case 'CONFIRMED_DISPENSATION':
      return EPharmacyMainTagType.ConfirmedDispensation;
    case 'POSSIBLE_DISPENSATION':
      return EPharmacyMainTagType.PossibleDispensation;
    default:
      return EPharmacyMainTagType.Unknown;
  }
};

export const parsePharmacyTagType = (type: string): EPharmacyTagType => {
  switch (type) {
    case 'IMMEDIATE_PAYMENT':
      return EPharmacyTagType.ImmediatePayment;
    case 'REQUEST_WITH_APP':
      return EPharmacyTagType.RequestWithApp;
    case 'REQUEST_WITH_CALL':
      return EPharmacyTagType.RequestWithCall;
    case 'OPEN_AT_LATE_NIGHT':
      return EPharmacyTagType.OpenAtLateNight;
    case 'OPEN_EVERYDAY':
      return EPharmacyTagType.OpenEveryday;
    case 'PEDIATRICS_MEDICINE':
      return EPharmacyTagType.PediatricsMedicine;
    case 'PSYCHIATRY_MEDICINE':
      return EPharmacyTagType.PsychiatryMedicine;
    case 'RESERVATION_WITH_APP':
      return EPharmacyTagType.ReservationWithApp;
    case 'PRESCRIPTION_REJECT':
      return EPharmacyTagType.PrescriptionReject;
    default:
      return EPharmacyTagType.Unknown;
  }
};

export const parsePharmacyPriceTagType = (type: string): EPharmacyPriceTagType => {
  switch (type) {
    case 'VERIFIED_PRICE':
      return EPharmacyPriceTagType.verifiedPrice;
    case 'REPORTED_PRICE':
    default:
      return EPharmacyPriceTagType.reportedPrice;
  }
};

export const parsePharmacyReviewTagVo = (data: PharmacyReviewTagsByRatingQuery): ReviewTagVo[] => {
  return data.pharmacyReviewTagsByRating.map((reviewTag) => ({ ...reviewTag }));
};

const getPharmacyOperationStatusV2 = (pharmacyOperatingStatus: PharmacyOperationStatusV2): EPharmacyOperationType => {
  switch (pharmacyOperatingStatus) {
    case PharmacyOperationStatusV2.Open:
      return EPharmacyOperationType.Open;
    case PharmacyOperationStatusV2.Closed:
      return EPharmacyOperationType.Closed;
    case PharmacyOperationStatusV2.DayOff:
      return EPharmacyOperationType.DayOff;
    case PharmacyOperationStatusV2.ClosedSoon:
      return EPharmacyOperationType.ClosedSoon;
    case PharmacyOperationStatusV2.Absent:
      return EPharmacyOperationType.Absent;
    case PharmacyOperationStatusV2.BreakTime:
      return EPharmacyOperationType.BreakTime;
    case PharmacyOperationStatusV2.BeforeOpen:
      return EPharmacyOperationType.BeforeOpen;
    case PharmacyOperationStatusV2.Unknown:
    default:
      return EPharmacyOperationType.Unknown;
  }
};

export const convertParkingType = (parkingType: PharmacyParkingType): EPharmacyParkingType => {
  switch (parkingType) {
    case PharmacyParkingType.Free:
      return EPharmacyParkingType.Free;
    case PharmacyParkingType.Paid:
      return EPharmacyParkingType.Paid;
    case PharmacyParkingType.Unavailable:
      return EPharmacyParkingType.Unavailable;
    default:
      return EPharmacyParkingType.Unavailable;
  }
};

export const parseGeneralListOfNearbyPharmacy = (
  data: FindGeneralListOfNearbyPharmacyV3ByUserQuery
): PharmacyModel[] => {
  const pharmacies = data.nearbyPharmacyV3ByUser?.map((pharmacy) => {
    const operationStatus = getPharmacyOperationStatusV2(pharmacy.operation.status);

    return new PharmacyModel({
      tel: pharmacy.contact.tel ?? '',
      safetyNumber: pharmacy.contact.safetyNumber ?? '',
      distance: pharmacy.location.distanceInMeters ?? 0,
      latitude: pharmacy.location.latitude,
      longitude: pharmacy.location.longitude,
      roadAddress: pharmacy.location.roadAddress ?? '',
      jibunAddress: pharmacy.location.jibunAddress ?? null,
      detailAddress: pharmacy.location.detailAddress ?? null,
      pharmacyName: pharmacy.location.pharmacyName,
      pharmacyId: pharmacy.pharmacyId,
      pharmacyPid: pharmacy.publicId,
      pickupCompletedCount: pharmacy.pickupCompletedCount,
      operationDisplay: pharmacy.operation.display ?? null,
      operationStatus: operationStatus,
      pharmacyNextOperationHour: pharmacy.pharmacyNextOperationHour.map(
        (nextOperationHour) =>
          new PharmacyNextOperationHourModel({
            earliestAvailableReservationHour: nextOperationHour.earliestAvailableReservationHour,
            earliestAvailableReservationHourString: nextOperationHour.earliestAvailableReservationHourString,
          })
      ),
      pharmacyDispensableMedicinePrices: pharmacy.dispensableMedicines
        ? new PharmacyDispensableMedicineModel({
            totalAmount: pharmacy.dispensableMedicines?.totalAmount
              ? Number(pharmacy.dispensableMedicines?.totalAmount)
              : null,
            dispensableMedicines: pharmacy.dispensableMedicines?.dispensableMedicines.map(
              (medicine) =>
                new DispensableMedicineModel({
                  medicineId: Number(medicine.pharmacyMedicine?.medicineId),
                  treatmentMedicineName: medicine.pharmacyMedicine?.treatmentMedicine?.name ?? '',
                  medicineName: medicine.pharmacyMedicine?.medicine?.medicineName ?? '',
                  userBasedStandard: medicine.pharmacyMedicine?.displayName ?? '',
                  price: medicine.pharmacyMedicine?.price ? Number(medicine.pharmacyMedicine?.price) : 0,
                  isSoldOut: false,
                  isAlternative: medicine.pharmacyMedicine?.isAlternativeMedicine ?? false,
                })
            ),
          })
        : null,
      partnershipType: pharmacy.partnership,
      mainTags: pharmacy.attributesV3.mainTags.map(
        (mainTag) =>
          new PharmacyMainTagModel({
            type: parsePharmacyMainTagType(mainTag.type),
            imageUrl: mainTag.imageUrl ?? '',
          })
      ),
      badges: pharmacy.attributesV3.badges.map(
        (badge) =>
          new PharmacyBadgeModel({
            type: badge.type,
            imageUrl: badge.imageUrl ?? '',
          })
      ),
      tags: pharmacy.attributesV3.tags.map(
        (tag) =>
          new PharmacyTagModel({
            type: parsePharmacyTagType(tag.type),
            imageUrl: tag.imageUrl ?? '',
          })
      ),
      priceTags: pharmacy.attributesV3.priceTags.map(
        (tag) =>
          new PharmacyPriceTagModel({
            type: parsePharmacyPriceTagType(tag.type),
            imageUrl: tag.imageUrl ?? '',
          })
      ),
      reviewSummary: {
        totalCount: pharmacy.reviewMeta.totalCount,
        averageRating: String(pharmacy.reviewMeta.averageRating),
      },
      diagnosisPrice: pharmacy.diagnosisPrice ?? null,
      isFavoritePharmacy: pharmacy.isFavoritePharmacy,
      parkingType: null,
      parkingDescription: '',
      directions: '',
    });
  });

  return pharmacies;
};

export const parsePickupListOfNearbyPharmacy = (data: FindPickupListOfNearbyPharmacyV3ByUserQuery): PharmacyModel[] => {
  const pharmacies = data.nearbyPharmacyV3ByUser?.map((pharmacy) => {
    const operationStatus = getPharmacyOperationStatusV2(pharmacy.operation.status);

    return new PharmacyModel({
      tel: pharmacy.contact.tel ?? '',
      safetyNumber: pharmacy.contact.safetyNumber ?? '',
      faxNumber: pharmacy.contact.faxNumber ?? '',
      distance: pharmacy.location.distanceInMeters ?? 0,
      latitude: pharmacy.location.latitude,
      longitude: pharmacy.location.longitude,
      roadAddress: pharmacy.location.roadAddress ?? '',
      jibunAddress: pharmacy.location.jibunAddress ?? null,
      detailAddress: pharmacy.location.detailAddress ?? null,
      pharmacyName: pharmacy.location.pharmacyName,
      pharmacyId: pharmacy.pharmacyId,
      pharmacyPid: pharmacy.publicId,
      pickupCompletedCount: pharmacy.pickupCompletedCount,
      operationDisplay: pharmacy.operation.display ?? null,
      operationStatus: operationStatus,
      pharmacyNextOperationHour: pharmacy.pharmacyNextOperationHour.map(
        (nextOperationHour) =>
          new PharmacyNextOperationHourModel({
            earliestAvailableReservationHour: nextOperationHour.earliestAvailableReservationHour,
            earliestAvailableReservationHourString: nextOperationHour.earliestAvailableReservationHourString,
          })
      ),
      pharmacyDispensableMedicinePrices: pharmacy.dispensableMedicines
        ? new PharmacyDispensableMedicineModel({
            totalAmount: pharmacy.dispensableMedicines?.totalAmount
              ? Number(pharmacy.dispensableMedicines?.totalAmount)
              : null,
            dispensableMedicines: pharmacy.dispensableMedicines?.dispensableMedicines.map(
              (medicine) =>
                new DispensableMedicineModel({
                  medicineId: Number(medicine.pharmacyMedicine?.medicineId),
                  treatmentMedicineName: medicine.pharmacyMedicine?.treatmentMedicine?.name ?? '',
                  medicineName: medicine.medicine?.medicineName ?? '',
                  userBasedStandard: medicine.pharmacyMedicine?.displayName ?? '',
                  price: medicine.pharmacyMedicine?.price ? Number(medicine.pharmacyMedicine?.price) : 0,
                  isSoldOut: false,
                  isAlternative: medicine.pharmacyMedicine?.isAlternativeMedicine ?? false,
                })
            ),
          })
        : null,
      partnershipType: pharmacy.partnership,
      mainTags: pharmacy.attributesV3.mainTags.map(
        (mainTag) =>
          new PharmacyMainTagModel({
            type: parsePharmacyMainTagType(mainTag.type),
            imageUrl: mainTag.imageUrl ?? '',
          })
      ),
      badges: pharmacy.attributesV3.badges.map(
        (badge) =>
          new PharmacyBadgeModel({
            type: badge.type,
            imageUrl: badge.imageUrl ?? '',
          })
      ),
      tags: pharmacy.attributesV3.tags.map(
        (tag) =>
          new PharmacyTagModel({
            type: parsePharmacyTagType(tag.type),
            imageUrl: tag.imageUrl ?? '',
          })
      ),
      priceTags: pharmacy.attributesV3.priceTags.map(
        (tag) =>
          new PharmacyPriceTagModel({
            type: parsePharmacyPriceTagType(tag.type),
            imageUrl: tag.imageUrl ?? '',
          })
      ),
      reviewSummary: {
        totalCount: pharmacy.reviewMeta.totalCount,
        averageRating: String(pharmacy.reviewMeta.averageRating),
      },
      diagnosisPrice: null,
      recommendationScore: pharmacy.attributesV3?.recommendationScore?.score,
      isFavoritePharmacy: pharmacy.isFavoritePharmacy,
      parkingType: null,
      parkingDescription: '',
      directions: '',
    });
  });

  return pharmacies;
};

export const parseRawPharmacyReview = (data: InfiniteData<PharmacyReviewsQuery>): InfiniteData<Review[]> => {
  return {
    ...data,
    pages: data.pages.map((el) =>
      el.pharmacyReviews.map((review) => {
        const createdAtDate = review.createdAt ? new Date(review.createdAt) : null;
        const formattedDate = createdAtDate
          ? `${createdAtDate.getFullYear()}.${String(createdAtDate.getMonth() + 1).padStart(2, '0')}.${String(createdAtDate.getDate()).padStart(2, '0')}`
          : null;

        return new Review({
          id: Number(review.id),
          title: review.title,
          content: review.content,
          tags: review.reviewTags.map(
            (reviewTag) =>
              new ReviewTag({
                reviewId: Number(review.id),
                tagId: Number(reviewTag.tag.tagId),
                text: String(reviewTag.tag.text),
              })
          ),
          rating: review.rating,
          writer: review.maskedHolderName ?? '',
          createdAt: formattedDate,
          diagnosisCategoryName: review.targetCategoryName ?? '',
          isLiked: false,
          isPrivate: review.isPrivate,
          likeCount: 0,
          isBlockedByUser: false,
          isCommentBlockedByUser: false,
          userId: Number(review.reviewContext.reviewHolderId),
          price: review.targetPrice ? formatPriceComma(review.targetPrice) : null,
          doctor: null,
          comment: null,
        });
      })
    ),
  };
};

export const parseNearbyPharmacySortsAndFiltersWithSubFilters = (
  data: PickupNearbyPharmacySearchTabQuery
): NearbyPharmacySortsAndFiltersVo => {
  return {
    filters:
      data.pickupNearbyPharmacySearchTab?.filters.map((filter) => {
        return new NearbyPharmacyFilterModel({
          activeIconUrl: filter.activeIconUrl ?? undefined,
          inactiveIconUrl: filter.inactiveIconUrl ?? undefined,
          description: filter.description ?? '',
          mode: mapToPharmacyFilterMode(filter.mode),
          title: filter.title ?? '',
          type: mapToPharmacySearchFilterType(filter.type),
          subFilters: filter.subFilters.map(
            (subFilter) =>
              new NearbyPharmacyFilterModel({
                activeIconUrl: subFilter.activeIconUrl ?? undefined,
                inactiveIconUrl: subFilter.inactiveIconUrl ?? undefined,
                description: subFilter.description ?? '',
                mode: mapToPharmacyFilterMode(subFilter.mode),
                title: subFilter.title ?? '',
                type: mapToPharmacySearchFilterType(subFilter.type),
                subFilters: [],
              })
          ),
        });
      }) ?? [],
    sorts: data.pickupNearbyPharmacySearchTab?.sortTypes.map((type) => mapToPharmacySortsVo(type)) ?? [],
  };
};

export const parseToNearbyPharmacyMedicineVo = (
  data: TreatmentMedicinesByTreatmentIdQuery
): NearbyPharmacyMedicineVo[] => {
  return data.treatmentMedicinesByTreatmentId.map((item) => {
    return {
      treatmentMedicineId: item.id,
      treatmentId: item.treatmentId,
      name: item.name,
      description: item.description ?? undefined,
      isNew: TreatmentMedicineService.isNewMedicine(item.id),
      dispensationUnits: item.dispensationUnits.map((dispensationUnit) => ({
        isDefault: dispensationUnit.isDefault,
        dispensationUnit: dispensationUnit.dispensationUnit,
        dispensationUnitDisplayName: dispensationUnit.dispensationUnitDisplayName,
        treatmentMedicineId: Number(dispensationUnit.treatmentMedicineId),
        treatmentMedicineUnitId: Number(dispensationUnit.treatmentMedicineUnitId),
      })),
      treatmentMedicineStrengths: [],
    };
  });
};

export const parseNearbyPharmacySearchTabsVo = (
  data: GeneralNearbyPharmacySearchTabsQuery
): NearbyPharmacySearchTabsVo[] => {
  return data.generalNearbyPharmacySearchTabs.map((item) => ({
    ...item,
    diagnosisTypeId: item.diagnosisTypeId ?? null,
    filters: item.filters.map((filter) => {
      return new NearbyPharmacyFilterModel({
        activeIconUrl: filter.activeIconUrl ?? undefined,
        inactiveIconUrl: filter.inactiveIconUrl ?? undefined,
        mode: mapToPharmacyFilterMode(filter.mode),
        title: filter.title ?? '',
        description: filter.description ?? '',
        type: mapToPharmacySearchFilterType(filter.type),
        subFilters: filter.subFilters.map((subFilter) => {
          return new NearbyPharmacyFilterModel({
            activeIconUrl: subFilter.activeIconUrl ?? undefined,
            inactiveIconUrl: subFilter.inactiveIconUrl ?? undefined,
            description: subFilter.description ?? '',
            mode: mapToPharmacyFilterMode(subFilter.mode),
            title: subFilter.title ?? '',
            type: mapToPharmacySearchFilterType(subFilter.type),
            subFilters: [],
          });
        }),
      });
    }),
    sortTypes: item.sortTypes.map((type) => mapToPharmacySortsVo(type)),
    treatmentMedicines: item.treatmentMedicines.map((medicine) => {
      return {
        treatmentMedicineId: medicine.id,
        treatmentId: medicine.treatmentId,
        name: medicine.name,
        description: medicine.description ?? undefined,
        isNew: TreatmentMedicineService.isNewMedicine(medicine.id),
        treatmentMedicineStrengths: medicine.treatmentMedicineStrengths.map((strength) => {
          return {
            treatmentMedicineStrengthId: strength.treatmentMedicineStrengthId,
            treatmentMedicineId: strength.treatmentMedicineId,
            unit: strength.unit,
            isDefault: strength.isDefault,
          };
        }),
        dispensationUnits: medicine.dispensationUnits.map((unit) => {
          return {
            isDefault: unit.isDefault,
            dispensationUnit: unit.dispensationUnit,
            dispensationUnitDisplayName: unit.dispensationUnitDisplayName,
            treatmentMedicineId: Number(unit.treatmentMedicineId),
            treatmentMedicineUnitId: Number(unit.treatmentMedicineUnitId),
          };
        }),
      };
    }),
  }));
};

export const parseNearbyPharmacyDetailV3ByUserVo = (
  data: GetNearbyPharmacyDetailV3ByUserQuery
): PharmacyModel | undefined => {
  if (!data.nearbyPharmacyDetailByPublicId) return;
  const pharmacy = data.nearbyPharmacyDetailByPublicId;

  const operationStatus = getPharmacyOperationStatusV2(pharmacy.operation.status);

  return new PharmacyModel({
    pharmacyPid: pharmacy.publicId,
    roadAddress: pharmacy.location.roadAddress ?? null,
    jibunAddress: pharmacy.location.jibunAddress ?? null,
    detailAddress: pharmacy.location.detailAddress ?? null,
    reviewSummary: pharmacy.reviewMeta,
    distance: pharmacy.location.distanceInMeters,
    latitude: pharmacy.location.latitude,
    longitude: pharmacy.location.longitude,
    pharmacyId: pharmacy.pharmacyId,
    pharmacyName: pharmacy.location.pharmacyName,
    pickupCompletedCount: pharmacy.pickupCompletedCount,
    operationDisplay: pharmacy.operation.display ?? '',
    operationStatus,
    tel: pharmacy.contact.tel ?? null,
    safetyNumber: pharmacy.contact.safetyNumber ?? null,
    partnershipType: pharmacy.partnership,
    mainTags: pharmacy.attributesV3.mainTags.map(
      (mainTag) =>
        new PharmacyMainTagModel({
          type: parsePharmacyMainTagType(mainTag.type),
          imageUrl: mainTag.imageUrl ?? '',
        })
    ),
    badges: pharmacy.attributesV3.badges.map(
      (badge) =>
        new PharmacyBadgeModel({
          type: badge.type,
          imageUrl: badge.imageUrl ?? '',
        })
    ),
    tags: pharmacy.attributesV3.tags.map(
      (tag) =>
        new PharmacyTagModel({
          type: parsePharmacyTagType(tag.type),
          imageUrl: tag.imageUrl ?? '',
        })
    ),
    priceTags: pharmacy.attributesV3.priceTags.map(
      (tag) =>
        new PharmacyPriceTagModel({
          type: parsePharmacyPriceTagType(tag.type),
          imageUrl: tag.imageUrl ?? '',
        })
    ),
    pharmacyNextOperationHour: pharmacy.pharmacyNextOperationHour.map(
      (nextOperationHour) =>
        new PharmacyNextOperationHourModel({
          earliestAvailableReservationHour: nextOperationHour.earliestAvailableReservationHour,
          earliestAvailableReservationHourString: nextOperationHour.earliestAvailableReservationHourString,
        })
    ),
    diagnosisPrice: null,
    reviews: pharmacy.numberOfReviewsByRating.map(
      (review) =>
        new PharmacyReviewModel({
          rating: review.rating,
          count: review.count,
        })
    ),
    todayDuty: pharmacy.operation.todayDuty
      ? new PharmacyDutyModel({
          weekDay: pharmacy.operation.todayDuty.weekDay,
          isClosed: pharmacy.operation.todayDuty.isClosed,
          openTime: pharmacy.operation.todayDuty.openTime.slice(0, -3),
          closeTime: pharmacy.operation.todayDuty.closeTime.slice(0, -3),
          hasBreakTime: pharmacy.operation.todayDuty.hasBreakTime,
          breakStartTime: pharmacy.operation.todayDuty.breakStartTime.slice(0, -3),
          breakEndTime: pharmacy.operation.todayDuty.breakEndTime.slice(0, -3),
        })
      : null,
    pharmacyDuties:
      pharmacy.operation.duties.map(
        (duty) =>
          new PharmacyDutyModel({
            weekDay: duty.weekDay,
            isClosed: duty.isClosed,
            openTime: duty.openTime.slice(0, -3),
            closeTime: duty.closeTime.slice(0, -3),
            hasBreakTime: duty.hasBreakTime,
            breakStartTime: duty.breakStartTime.slice(0, -3),
            breakEndTime: duty.breakEndTime.slice(0, -3),
          })
      ) ?? [],
    prescribedMedicineInfoWithDoseDays: pharmacy.prescribedMedicineInfoWithDoseDays.map(
      (medicine) =>
        new MedicineInfoWithDoseDaysModel({
          medicineSummary: medicine.medicineSummary
            ? {
                medicineId: medicine.medicineSummary.medicineId,
                medicineName: medicine.medicineSummary.medicineName,
                medicineImageUrl: medicine.medicineSummary.medicineImageUrl,
                hasDetailInfo: medicine.medicineSummary.hasDetailInfo,
                pharmaceuticalEffectLiteracy: medicine.medicineSummary.pharmaceuticalEffectLiteracy,
                medicinePid: medicine.medicineSummary.publicId,
                medicineStockStatus: {
                  status: medicine.medicineSummary.medicineDispensability.dispensabilityStatus,
                  imageUrl: medicine.medicineSummary.medicineDispensability.iconImageUrl,
                },
              }
            : null,
        })
    ),
    isFavoritePharmacy: pharmacy.isFavoritePharmacy,
    parkingType: pharmacy.location.parkingType
      ? {
          name: pharmacy.location.parkingType.name,
          type: convertParkingType(pharmacy.location.parkingType.type),
        }
      : null,
    parkingDescription: pharmacy.location.parkingDescription || null,
    directions: pharmacy.location.directions || null,
  });
};
